import { Gift, Heart, Signpost, Ticket, UsersThree } from '@phosphor-icons/react';
import { daysLeft, FAMILY_WISHES, LEISURE_TEXT, leftLabel, sortVouchers } from '@shh/shared';
import { Avatar, Icon, Sheet, Text, cn } from '@shh/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { IdeaForm, VoucherForm } from '../../components/leisureForms';
import { useFamily } from '../../data/family';
import {
  Empty,
  cardLink,
  SectionHead,
  SuggestionCard,
  useLeisureHint,
  useLeisureRights,
} from './common';
import { openIdeas } from './IdeasPage';
import { locale } from '../../lib/locale';
import { funText } from '../../i18n/fun';

/**
 * "Freizeit": the nice things that get lost in everyday life – ideas for the weekend, the
 * vouchers from the drawer and everybody's wish lists. One glance, then into the list.
 * On wide screens the three sit side by side.
 */
export function FreizeitPage() {
  const f = useFamily();
  const rights = useLeisureRights();
  const hint = useLeisureHint();
  const [adding, setAdding] = useState<'idea' | 'voucher' | null>(null);
  const ideas = openIdeas(f).slice(0, 3);
  const vouchers = sortVouchers(f.vouchers.filter((v) => !v.used && !expired(f.today, v.until)));
  const lists = [
    ...f.members.map((m) => ({ id: m.id, name: m.name, member: m })),
    { id: FAMILY_WISHES, name: funText.family, member: undefined },
  ];
  const openWishes = (owner: string) =>
    f.wishes.filter((w) => w.owner === owner && !w.received).length;

  return (
    <>
      <Text tone="secondary">{funText.intro}</Text>
      {hint && <SuggestionCard hint={hint} />}

      <div className="grid grid-cols-1 gap-section xl:grid-cols-3 xl:items-start">
        <section aria-labelledby="ideas" className="flex flex-col gap-gap-md">
          <SectionHead
            id="ideas"
            title={funText.ideas}
            link={{ to: '/freizeit/ideen', label: funText.allIdeas(String(openIdeas(f).length)) }}
            onAdd={() => setAdding('idea')}
            addLabel={funText.idea}
          />
          {ideas.length ? (
            <ul className="flex flex-col gap-gap-sm">
              {ideas.map((i) => (
                <li key={i.id}>
                  <Link
                    to="/freizeit/ideen"
                    className={cn(cardLink, 'flex items-center gap-gap-md p-inset-sm')}
                  >
                    <Icon icon={Signpost} size="lg" className="shrink-0 text-accent-secondary" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-bold">{i.title}</span>
                      <span className="text-sm text-text-secondary">
                        {LEISURE_TEXT[locale].place[i.place]}
                      </span>
                    </span>
                    {i.hearts.length > 0 && (
                      <span className="flex items-center gap-gap-xs font-bold text-reward-kudos">
                        <Icon icon={Heart} size="sm" weight="fill" />
                        <span>
                          {i.hearts.length}
                          <span className="sr-only">{funText.hearts}</span>
                        </span>
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <Empty icon={Signpost}>{funText.ideasEmpty}</Empty>
          )}
        </section>

        <section aria-labelledby="vouchers" className="flex flex-col gap-gap-md">
          <SectionHead
            id="vouchers"
            title={funText.vouchers}
            link={{
              to: '/freizeit/gutscheine',
              label: funText.openVouchers(String(vouchers.length)),
            }}
            onAdd={rights.canPlan ? () => setAdding('voucher') : undefined}
            addLabel={funText.voucher}
          />
          {vouchers.length ? (
            <ul className="grid grid-cols-2 gap-gap-sm">
              {vouchers.slice(0, 4).map((v) => {
                const left = v.until ? daysLeft(f.today, v.until) : undefined;
                const soon = left !== undefined && left >= 1 && left <= 31;
                return (
                  <li key={v.id}>
                    <Link
                      to="/freizeit/gutscheine"
                      className={cn(
                        'flex h-full flex-col gap-gap-xs rounded-card border-2 p-inset-sm outline-none',
                        'focus-visible:outline-3 focus-visible:outline-border-focus',
                        soon
                          ? 'border-feedback-warning bg-feedback-warning-surface'
                          : 'border-border-subtle bg-surface-raised',
                      )}
                    >
                      <span className="font-bold">
                        {v.title}
                        {v.value ? ` · ${v.value}` : ''}
                      </span>
                      <span className="text-sm text-text-secondary">
                        {v.until ? leftLabel(f.today, v.until, locale) : funText.noExpiry}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <Empty icon={Ticket}>{funText.vouchersEmpty}</Empty>
          )}
        </section>

        <section aria-labelledby="wishes" className="flex flex-col gap-gap-md">
          <SectionHead id="wishes" title={funText.wishLists} />
          <ul className="grid grid-cols-3 gap-gap-sm sm:grid-cols-4 xl:grid-cols-3">
            {lists.map((l) => (
              <li key={l.id}>
                <Link
                  to={`/freizeit/wuensche/${l.id}`}
                  className={cn(
                    cardLink,
                    'flex h-full flex-col items-center gap-gap-xs p-inset-sm text-center',
                  )}
                >
                  {l.member ? (
                    <Avatar
                      name={l.member.name}
                      color={l.member.color}
                      emoji={l.member.emoji}
                      size="md"
                      decorative
                    />
                  ) : (
                    <span className="grid size-avatar-md place-items-center rounded-avatar bg-accent-secondary text-accent-on-accent">
                      <Icon icon={UsersThree} size="md" weight="fill" />
                    </span>
                  )}
                  <span className="font-bold">{l.name}</span>
                  <span className="text-sm text-text-secondary">
                    {funText.wishes(openWishes(l.id))}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Text size="sm" tone="muted" className="flex items-start gap-gap-xs">
            <Icon icon={Gift} size="sm" className="mt-0.5 shrink-0" />
            {funText.claimsHint}
          </Text>
        </section>
      </div>

      <Sheet
        isOpen={adding !== null}
        onOpenChange={(o) => !o && setAdding(null)}
        title={adding === 'voucher' ? funText.newVoucher : funText.newIdea}
        isDismissable={false}
      >
        {adding === 'idea' && <IdeaForm onDone={() => setAdding(null)} />}
        {adding === 'voucher' && <VoucherForm onDone={() => setAdding(null)} />}
      </Sheet>
    </>
  );
}

export const expired = (today: string, until?: string) => Boolean(until && until < today);
